import { C } from "./theme"

export default function PrivacyPolicy({ onBack }) {
  const wrap = {
    minHeight: '100vh', background: C.bg, color: C.text,
    fontFamily: "'Outfit', sans-serif", padding: '24px 16px',
  }
  const container = { maxWidth: 720, margin: '0 auto' }
  const h1 = {
    fontFamily: C.serif, fontSize: 38, fontWeight: 400, letterSpacing: -0.8,
    color: C.text, marginBottom: 8,
  }
  const h2 = {
    fontFamily: C.serif, fontSize: 22, fontWeight: 400, letterSpacing: -0.3,
    color: C.text, marginTop: 32, marginBottom: 10,
  }
  const p = { fontSize: 15, lineHeight: 1.7, color: C.muted, marginBottom: 14 }
  const updated = {
    fontSize: 10, color: C.muted, marginBottom: 28,
    fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase',
  }

  return (
    <div style={wrap}>
      <div style={container}>
        {onBack && (
          <button onClick={onBack} style={{
            background: C.glass,
            border: `1px solid ${C.glassBorder}`,
            backdropFilter: 'blur(14px) saturate(160%)',
            WebkitBackdropFilter: 'blur(14px) saturate(160%)',
            color: C.muted,
            cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            padding: '8px 16px', marginBottom: 24, borderRadius: 999, minHeight: 40,
          }}>
            ← Back
          </button>
        )}

        <h1 style={h1}>Privacy <span style={{ fontStyle: 'italic' }}>policy</span></h1>
        <p style={updated}>Last updated: September 4, 2026</p>

        <p style={p}>
          Settle ("we", "our", or "us") operates the Settle mobile application and website
          (collectively, the "Service"). This Privacy Policy explains how we collect, use, and
          protect your personal information when you use our Service.
        </p>

        <h2 style={h2}>1. Information We Collect</h2>
        <p style={p}>
          <strong style={{ color: C.text }}>Account Information:</strong> When you create an account,
          we collect your email address and, if you use Google Sign-In, your Google profile name and
          email. We do not store your Google password.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>Plan Data:</strong> Information you enter into your
          relocation plan, including city preferences, salary range, family size, housing preferences,
          and work area. This data is stored securely in our database and is only accessible to you.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>AI Assistant Data:</strong> Questions you ask our AI
          assistant, together with the facts from your own plan needed to answer them, are processed
          by our AI provider (Anthropic) to generate responses. Your conversation with the assistant
          is kept on your device and is not permanently stored on our servers.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>Receipt Photos:</strong> If you scan a receipt, the image
          is sent to our server and on to our AI provider to read the amount and merchant. Only the
          resulting expense is saved. We do not keep the image.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>Community Data (mobile app):</strong> If you opt in to
          the social features, we store the profile card other members see (first name, age, and
          nationality if you give it), the gatherings and groups you host or join, and the messages
          you send in group chats. Messages are stored on our servers so the group can read them.
          If you tell us your gender, it is used only to show women-only gatherings to women. A
          gathering's venue is shared only with guests the host has approved. We also store reports
          and blocks you file so we can act on them.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>Push Notification Tokens:</strong> If you enable push
          notifications, we store your device push subscription token to deliver notifications you
          have opted into.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>Analytics:</strong> We use Plausible Analytics, a
          privacy-focused analytics service that does not use cookies, does not collect personal
          data, and does not track individual users across sites.
        </p>

        <h2 style={h2}>2. How We Use Your Information</h2>
        <p style={p}>
          We use your information to provide, maintain, and improve the Service, including generating
          personalized relocation plans, powering AI-assisted recommendations, sending notifications
          you have opted into, and analyzing aggregate usage patterns to improve our product.
        </p>

        <h2 style={h2}>3. Data Storage and Security</h2>
        <p style={p}>
          Your data is stored on Supabase, a secure cloud database platform with encryption at rest
          and in transit. We use Row Level Security (RLS) policies to ensure each user can only
          access their own data. All communication between your device and our servers uses HTTPS
          encryption.
        </p>

        <h2 style={h2}>4. Third-Party Services</h2>
        <p style={p}>
          We use the following third-party services: Supabase (authentication and database), Anthropic
          (AI chat and smart plan generation), Google (OAuth sign-in), and Plausible (privacy-focused
          analytics). Each service has its own privacy policy governing its handling of your data.
        </p>

        <h2 style={h2}>5. Data Sharing</h2>
        <p style={p}>
          We do not sell, rent, or share your personal information with third parties for marketing
          purposes. We may share anonymized, aggregate data for analytics. We may disclose information
          if required by law or to protect the rights and safety of our users.
        </p>
        <p style={p}>
          <strong style={{ color: C.text }}>What other members can see.</strong> Your plan, budget,
          and paperwork are never visible to other users. If you opt in to the social features, other
          members see only what those features are built to show: your profile card when you ask to
          join something or host it, your messages in the group chats you are part of, and your seat
          in a group you joined.
        </p>

        <h2 style={h2}>6. Your Rights</h2>
        <p style={p}>
          You have the right to access, correct, or delete your personal data at any time. Most of it
          you can see and edit in the app directly. In the mobile app you can delete your account
          yourself: open Home, scroll to the bottom, and choose "Delete my account". On the web,
          email us at support@settletheapp.com from the address on the account. Deleting your account
          removes your plan, budget, spending, paperwork progress, profile, seats, gatherings and
          messages. You can opt out of push notifications at any time through the app settings.
        </p>

        <h2 style={h2}>7. Data Retention</h2>
        <p style={p}>
          We retain your data for as long as your account is active. If you delete your account, we
          will delete your personal data within 30 days, except where retention is required by law.
        </p>

        <h2 style={h2}>8. Age</h2>
        <p style={p}>
          Settle is for adults. You must be 18 or older to create an account. The social features
          involve arranging to meet other people in person, which is not for children. We do not
          knowingly collect personal information from anyone under 18, and if we learn that we have,
          we will take steps to delete it promptly.
        </p>

        <h2 style={h2}>9. Changes to This Policy</h2>
        <p style={p}>
          We may update this Privacy Policy from time to time. We will notify you of any material
          changes by posting the updated policy in the app and updating the "Last updated" date above.
        </p>

        <h2 style={h2}>10. Contact Us</h2>
        <p style={p}>
          If you have questions about this Privacy Policy, please contact us at
          support@settletheapp.com.
        </p>

        <div style={{ height: 60 }} />
      </div>
    </div>
  )
}
